import { configureApi, loginApiV1AuthLoginPost } from '@closet-theory/api';
import { createSessionFetch, type RefreshedSession } from '@ui/console/session-fetch';
import {
  invalidatesSession,
  isStylistRole,
  resolveApiBaseUrl,
  tokenIsExpired,
} from './auth-policy';

const TOKEN_KEY = 'closet-theory-stylist-web-access-token';
const REFRESH_KEY = 'closet-theory-stylist-web-refresh-token';
const ROLE_KEY = 'closet-theory-stylist-web-role';
export const SESSION_EXPIRED_EVENT = 'closet-theory-stylist-web-session-expired';
const configuredApiBaseUrl: unknown = import.meta.env.VITE_API_BASE_URL;
if (import.meta.env.PROD && typeof configuredApiBaseUrl !== 'string') {
  throw new Error('VITE_API_BASE_URL is required for production Stylist builds.');
}
const API_BASE_URL = resolveApiBaseUrl(
  typeof configuredApiBaseUrl === 'string' ? configuredApiBaseUrl : 'http://localhost:8000',
  import.meta.env.PROD,
);

export function apiBaseUrl(): string {
  return API_BASE_URL;
}

function storeSession(session: RefreshedSession): void {
  sessionStorage.setItem(TOKEN_KEY, session.access_token);
  sessionStorage.setItem(REFRESH_KEY, session.refresh_token);
  sessionStorage.setItem(ROLE_KEY, session.role);
}

// Access tokens live 15 minutes; without this every read 401'd a quarter of
// an hour into a session. See @ui/console/session-fetch.
const session = createSessionFetch({
  apiBaseUrl: API_BASE_URL,
  readTokens: () => ({
    accessToken: sessionStorage.getItem(TOKEN_KEY) ?? undefined,
    refreshToken: sessionStorage.getItem(REFRESH_KEY) ?? undefined,
  }),
  writeSession: (refreshed) => {
    if (!isStylistRole(refreshed.role)) return false;
    storeSession(refreshed);
    return true;
  },
  onSessionExpired: () => expireSession(),
  isExpired: (token) => tokenIsExpired(token),
});

/** fetch for the API origin that carries the session's bearer and refreshes it. */
export const stylistFetch = session.fetch;

/**
 * The stored access token while the session is still live - valid, or expired
 * but refreshable (stylistFetch refreshes it before sending).
 */
export function accessToken(): string | undefined {
  const token = sessionStorage.getItem(TOKEN_KEY) ?? undefined;
  const refresh = sessionStorage.getItem(REFRESH_KEY);
  const role = sessionStorage.getItem(ROLE_KEY);
  if (!token || !role || !isStylistRole(role) || (tokenIsExpired(token) && !refresh)) {
    signOut();
    return undefined;
  }
  return token;
}

export function configureStylistApi(): void {
  configureApi(API_BASE_URL, accessToken(), { fetch: stylistFetch });
}

export function configurePublicStylistApi(): void {
  configureApi(API_BASE_URL, undefined, { fetch: stylistFetch });
}

export async function signIn(email: string, password: string): Promise<void> {
  configurePublicStylistApi();
  const result = await loginApiV1AuthLoginPost({ body: { email, password } });
  if (result.error || !result.data?.access_token) {
    throw new Error('Email or password is incorrect.');
  }
  if (!isStylistRole(result.data.role)) {
    signOut();
    throw new Error('This account is not authorized for the Stylist workspace.');
  }
  storeSession(result.data);
  configureStylistApi();
}

export function signOut(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
  sessionStorage.removeItem(ROLE_KEY);
  configurePublicStylistApi();
}

function expireSession(): void {
  signOut();
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
}

/** A 401 that survived stylistFetch's refresh and retry: the session is over. */
export function handleUnauthorized(status: number | undefined): void {
  if (!invalidatesSession(status)) return;
  expireSession();
}
